import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useMemo } from 'react';
import { Pressable as RNPressable } from 'react-native';
import MoreOptionsButton from 'src/components/10-atoms/dropdown-menu/MoreOptionsButton';
import DeleteIcon from 'src/components/10-atoms/icons/DeleteIcon';
import EditIcon from 'src/components/10-atoms/icons/EditIcon';
import FeatherIcon from 'src/components/10-atoms/icons/FeatherIcon';
import HistoryIcon from 'src/components/10-atoms/icons/HistoryIcon';
import SortableItemIcon from 'src/components/10-atoms/icons/SortableItemIcon';
import Row from 'src/components/10-atoms/layout/Row';
import Pressable from 'src/components/10-atoms/pressables/Pressable';
import Typography from 'src/components/10-atoms/typography/Typography';
import { TypographyFontWeight } from 'src/customizations/themes/10-parts/typography';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import createThemedStyle from 'src/customizations/themes/utils/createThemedStyles';
import useI18n from 'src/modules/translations/hooks/useI18n';
const TabItem = ({ tab, isActive, editable = true, sortable = false, to, onTabChange, onEditTab, onDeleteTab, onViewHistory, }) => {
    const [{ theme, styles }] = useTheme(stylesGenerator);
    const { translate } = useI18n();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, } = useSortable({ id: tab.id, disabled: !sortable });
    const sortableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 0,
        position: 'relative',
    };
    const menuOptions = useMemo(() => {
        const options = [];
        if (editable && onEditTab) {
            options.push({
                Icon: EditIcon,
                title: translate('Edit'),
                onPress: () => onEditTab(tab),
            });
        }
        if (editable && onDeleteTab) {
            options.push({
                Icon: DeleteIcon,
                title: translate('Delete'),
                onPress: () => onDeleteTab(tab),
            });
        }
        if (onViewHistory) {
            options.push({
                Icon: HistoryIcon,
                title: translate('View history'),
                onPress: () => onViewHistory(tab),
            });
        }
        return options;
    }, [onEditTab, onDeleteTab, onViewHistory, translate, tab, editable]);
    return (React.createElement("div", { ref: setNodeRef, style: sortable ? sortableStyle : undefined },
        React.createElement(Pressable, { to: to, onPress: () => onTabChange(tab.id) },
            React.createElement(Row, { style: [styles.tab, isActive && styles.activeTab], xAxis: "space-between", yAxis: "center" },
                sortable && (React.createElement("div", { ...attributes, ...listeners, style: {
                        cursor: 'grab',
                        touchAction: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        marginRight: theme.spacing.standard.xs,
                    } },
                    React.createElement(SortableItemIcon, { size: theme.iconSize.sm, color: theme.palette.icon.secondary }))),
                React.createElement(Typography, { flex: true, fontWeight: isActive ? TypographyFontWeight.MEDIUM : TypographyFontWeight.REGULAR }, tab.data.title),
                menuOptions.length > 0 && (React.createElement(RNPressable, { onPress: (e) => e?.stopPropagation() },
                    React.createElement(MoreOptionsButton, { options: menuOptions, renderButton: () => (React.createElement(FeatherIcon, { name: "more-vertical", size: theme.iconSize.sm, color: theme.palette.icon.primary })) })))))));
};
const stylesGenerator = createThemedStyle(({ theme, deviceType }) => ({
    tab: deviceType.isPhone ? {
        paddingLeft: theme.spacing.standard.md,
        paddingRight: theme.spacing.standard.sm,
        paddingVertical: theme.spacing.standard.xs,
        borderRadius: theme.borderRadius.standard.xxl,
        overflow: 'hidden',
        marginHorizontal: theme.spacing.page,
    } : {
        borderTopRightRadius: theme.borderRadius.standard.xxl,
        borderBottomRightRadius: theme.borderRadius.standard.xxl,
        overflow: 'hidden',
        paddingLeft: theme.spacing.page,
        paddingRight: theme.spacing.standard.xs,
        marginRight: theme.spacing.page,
        paddingVertical: theme.spacing.standard.xs,
    },
    activeTab: {
        backgroundColor: theme.palette.primary.subtle,
    },
}));
export default TabItem;
